import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X as XIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Add / edit an in-yard service for a container.
 *
 * Props:
 *   container – { container_number, iso_type, last_carrier, ... }
 *   entry     – optional existing yard_service row (edit mode)
 *   onClose   – () => void
 *   onSaved   – () => void       — parent should reload after save
 */
export default function YardServiceDialog({ container, entry, onClose, onSaved }) {
  const editMode = !!entry;
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    service_code: entry?.service_code || "PTI",
    qty: entry?.qty ?? 1,
    performed_at: entry?.performed_at ? entry.performed_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
    notes: entry?.notes || "",
    unit_price_override: entry?.unit_price ?? "",
  });

  useEffect(() => {
    if (!container?.container_number) return;
    setLoading(true);
    api
      .get(`/yard-services/catalog?container_number=${encodeURIComponent(container.container_number)}`)
      .then((r) => setCatalog(r.data))
      .catch(() => toast.error("Failed to load service catalog"))
      .finally(() => setLoading(false));
  }, [container?.container_number]);

  const selected = useMemo(
    () => (catalog?.services || []).find((s) => s.code === form.service_code),
    [catalog, form.service_code],
  );
  const tariffRate = selected?.unit_price ?? 0;

  // When the user picks a service, snap the price to the tariff rate unless
  // they've already typed a manual override.
  const onServiceChange = (code) => {
    const svc = (catalog?.services || []).find((s) => s.code === code);
    setForm((f) => ({
      ...f,
      service_code: code,
      unit_price_override: svc ? svc.unit_price : f.unit_price_override,
    }));
  };

  const overrideNum = form.unit_price_override === "" || form.unit_price_override === null
    ? tariffRate
    : Number(form.unit_price_override);
  const total = Math.max(0, Number(form.qty || 0)) * Math.max(0, overrideNum || 0);

  const save = async () => {
    if (!form.service_code) return toast.error("Pick a service");
    if (!(Number(form.qty) > 0)) return toast.error("Qty must be greater than 0");
    setSaving(true);
    try {
      const body = {
        container_number: container.container_number,
        service_code: form.service_code,
        qty: Number(form.qty),
        performed_at: form.performed_at ? new Date(form.performed_at).toISOString() : null,
        notes: form.notes.trim() || null,
        unit_price_override:
          form.unit_price_override === "" || form.unit_price_override === null
            ? null
            : Number(form.unit_price_override),
      };
      if (editMode) {
        await api.patch(`/yard-services/${entry.id}`, body);
        toast.success("Service updated");
      } else {
        await api.post(`/yard-services`, body);
        toast.success("Service logged");
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save service";
      toast.error(typeof msg === "string" ? msg : "Failed to save service");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="yard-service-dialog"
    >
      <div className="bg-white border border-gray-300 max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-display text-xl font-bold">
              {editMode ? "Edit yard service" : "Log yard service"}
            </h2>
            <div className="text-xs text-gray-500 font-mono mt-0.5">
              {container?.container_number} · ISO {container?.iso_type || "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            data-testid="yard-service-dialog-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading tariff…
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {catalog && !catalog.tariff_present && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded-sm">
                No tariff configured for carrier <b>{catalog.carrier_code || "—"}</b>. You can still
                log the service but you'll need to enter the price manually.
              </div>
            )}

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Service *
              </label>
              <select
                value={form.service_code}
                onChange={(e) => onServiceChange(e.target.value)}
                className="ps-input w-full"
                data-testid="yard-service-code"
              >
                {(catalog?.services || []).map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label} {s.unit_price > 0 ? `— $${s.unit_price.toFixed(2)}/${s.unit}` : "(no rate)"}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Qty {selected?.unit === "day" ? "(days)" : ""}
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: e.target.value })}
                  className="ps-input w-full"
                  data-testid="yard-service-qty"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Unit price
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.unit_price_override}
                  onChange={(e) => setForm({ ...form, unit_price_override: e.target.value })}
                  className="ps-input w-full font-mono"
                  data-testid="yard-service-unit-price"
                />
                {selected && selected.unit_price > 0 && Number(form.unit_price_override) !== selected.unit_price && (
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    Tariff rate is ${selected.unit_price.toFixed(2)} — this is an override.
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Performed at
              </label>
              <input
                type="datetime-local"
                value={form.performed_at}
                onChange={(e) => setForm({ ...form, performed_at: e.target.value })}
                className="ps-input w-full"
                data-testid="yard-service-performed-at"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Notes
              </label>
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="ps-input w-full"
                placeholder="Technician, findings, etc."
                data-testid="yard-service-notes"
              />
            </div>

            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
              <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-gray-600">Line total</span>
              <span className="font-mono text-lg font-bold" data-testid="yard-service-total">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="yard-service-cancel">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="ps-btn ps-btn-primary"
            data-testid="yard-service-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editMode ? "Save changes" : "Log service"}
          </button>
        </div>
      </div>
    </div>
  );
}
